// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import BroadcastHeader from "./BroadcastHeader";
import FitLine from "../../live/FitLine";
import FitRow from "./FitRow";
import type { WrpfSponsor } from "../../handoff/types";
import { AttemptCells, bestLabel, ClockBox, JudgeLightsBox, RecordTag, recordStyle, totalLabel } from "./overlayParts";
import type { OverlayView } from "./overlayView";
import { LIFT_LABELS } from "./overlayView";

import styles from "./BroadcastOverlayView.module.scss";

interface Props {
  view: OverlayView;
  sponsors: ReadonlyArray<WrpfSponsor>;
}

// Fusión elegida: barra superior de B (torneo y sponsors) + franja inferior de A, con luces y reloj al final de
// la misma franja para que todo lo del intento se lea en un solo lugar. Cortes en diagonal y texturas de marca
// según la referencia visual del 28/9; aprobado para producción.
const BroadcastOverlayView = ({ view, sponsors }: Props) => {
  const athlete = view.athlete;
  const barCaption = view.result === "good" ? "Válido" : view.result === "noLift" ? "Nulo" : athlete?.attemptLabel;
  return (
    <div className={styles.root}>
      <BroadcastHeader tournamentName={view.tournamentName} sessionLabel={view.sessionLabel} sponsors={sponsors} />

      <footer className={styles.footer}>
        {athlete?.record ? (
          <div className={styles.recordBand} style={recordStyle(athlete.record.color, athlete.record.textColor)}>
            Intento de {athlete.record.label.toLowerCase()}
          </div>
        ) : null}
        <div className={styles.strip}>
          {athlete !== null ? (
            <section key={athlete.entryKey} className={styles.athlete} aria-label="Atleta en tarima">
              <div className={styles.bar} data-result={view.result ?? undefined}>
                <span className={styles.barCaption}>{barCaption}</span>
                <span className={styles.barWeight}>
                  {athlete.weightLabel}
                  <small>kg</small>
                </span>
              </div>

              <div className={styles.identity}>
                <FitLine
                  className={styles.name}
                  maxFontSize={60}
                  singleLineMinFontSize={44}
                  wrapMaxFontSize={36}
                  minFontSize={22}
                  maxLines={2}
                >
                  {athlete.name}
                </FitLine>
                <ul className={styles.details}>
                  {athlete.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
                <FitRow
                  className={styles.origin}
                  ariaLabel="Procedencia"
                  items={athlete.origin}
                  maxFontSize={21}
                  minFontSize={14}
                />
              </div>

              <div className={styles.lifts}>
                {athlete.progress.rows.map((row) => (
                  <div key={row.lift} className={styles.liftRow} data-current={row.current ? "true" : undefined}>
                    <span className={styles.liftLabel}>{LIFT_LABELS[row.lift]}</span>
                    {row.current ? (
                      <AttemptCells row={row} attemptNumber={athlete.attemptNumber} className={styles.cells} />
                    ) : (
                      <span className={styles.best} data-state={row.state}>
                        {bestLabel(row)}
                        <RecordTag row={row} className={styles.recordTag} />
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {athlete.progress.showTotal ? (
                <div className={styles.total} data-bombed={athlete.progress.bombed ? "true" : undefined}>
                  <span>{athlete.progress.bombed ? "Sin total" : "Total"}</span>
                  <strong>{totalLabel(athlete.progress)}</strong>
                </div>
              ) : null}
            </section>
          ) : (
            <section className={styles.waiting} aria-label="Atleta en tarima">
              {view.waitingForNext ? "Esperando al siguiente atleta" : null}
            </section>
          )}

          <JudgeLightsBox lights={view.lights} className={styles.lights} />
          <ClockBox seconds={view.clockSeconds} running={view.clockRunning} className={styles.clock} />
          <span className={styles.endSlash} aria-hidden="true" />
        </div>
      </footer>
    </div>
  );
};

export default BroadcastOverlayView;
